
import { Space } from '@mantine/core'
import { DateTime } from 'luxon'
import { GetServerSideProps } from 'next'
import Head from 'next/head'

import { useRouter } from 'next/router'

import EmailSignupBanner from '../../components/EmailSignupBanner'
import { MenuId } from '../../components/Header'
import Layout from '../../components/Layout'
import RelatedVideos, { AncillaryLinks, BigVideo, VideoList } from '../../components/Video'
import { getJWTTokenFromUser, getUserInformation, getVideoAncillaryLinks, getVideoFromSlug, redirectUrl } from '../../lib/data'
import { RusAuthProps, RusContextProps, SearchJson, Video, VideoAncillaryLink, VideoSearchResult } from '../../lib/definitions'

import styles from './[video].module.scss'
import Link from 'next/link'
import useAuth from '../../lib/useAuth'
import { encode } from 'querystring'

//This isn't cached for logged on users
//But is cached if you're not
type Data = {
  video : Video |null,
  dateProcessed: string,
  relatedVideos: Video [],
  ancillaryLinks: VideoAncillaryLink [],
  videosInSeries: Video [],
  trailer: Video | null
    }

export default function VideoPage({data} : {data: Data} & RusAuthProps & RusContextProps)
{

  const {userInformation} = useAuth();

  //const { video } = router.query

  //should only get here if video is in the future AND it's not accessible
  //OR it's in the past (and may or may not be accessible)
  //OR it's in the future but NOT a live video

  let description =<>{data.video?.description}</>

  if (data.video?.htmlDescription && data.video.isSeries)
  {
    let html = {__html: data.video?.htmlDescription};
    description = <div dangerouslySetInnerHTML={html}></div>
  }

  let id = (data.video?data.video.id:0);

  if (!data.video?.isSeries)
  {
 
  return(
  <Layout menuId={MenuId.online}>

  <Head>
      <title>{data.video?.title}</title>
      <meta name="description" content={data.video?.description}/>
      <meta property='og:title' content={data.video?.title}/>
      <meta property='og:description' content={`An online art video from Raw Umber Studios. ${data.video?.description}`}/>
      <meta property='og:image' content={`https://rawumberstudios.com/rus-videos/images/previews/${data.video?.thumbnail}`}/>
      <meta property='og:image-width' content='1920'/>
      <meta property='og:image-height' content='1080'/>

  </Head>
  <div className = {styles.mainContent}>
  <h1 className={styles.h1}>{data.video?.title}</h1>
  <h2 className={styles.h2}>{data.video?.tutor?.name}</h2>

  <div>
    <div>{description}</div>
    <div><BigVideo key={"bv-" + data.video?.id} video={data.video} dateProcessed={data.dateProcessed}/></div>
    <Space h="lg" />
    {data.video?.accessible&&<AncillaryLinks links={data.ancillaryLinks} showAll/>}
    {(data.video?.chatId && userInformation?.logged_in) && <Link href={`/videos/${data.video.slug}/transcript`}>Chat transcript</Link>}
    <RelatedVideos videos={data.relatedVideos} videoId={id}/>
  </div>
 
  </div>
  <Space h="lg" />
  <EmailSignupBanner/>
</Layout>
  );
  }

  //it's a series. Should always be accessible.

  let image = "https://rawumberstudios.com/rus-videos/images/previews/" + data.video.thumbnail;


    return(

      <Layout menuId={MenuId.online}>

      <Head>
          <title>{data.video?.title}</title>
          <meta name="description" content={data.video?.description}/>
      </Head>
      <div className = {styles.mainContent}>
      <h1 className={styles.h1}>{data.video?.title}</h1>
      <h2 className={styles.h2}>{data.video?.tutor?.name}</h2>
    
      <div>
        <div>{description}</div>
        <Space h="lg"/>
        <div>
        {!data.trailer && <><div><img className={styles.bigImage} src={image}/></div></>}
        {data.trailer &&  <><div><BigVideo video={data.trailer} dateProcessed={data.dateProcessed}/></div></>}
        </div>
        <Space h="lg" />
        <VideoList videos={data.videosInSeries} dateProcessed={data.dateProcessed}/>
      </div>
     
      </div>
      <Space h="lg" />
      <EmailSignupBanner/>
    </Layout>
      );    

}



export const getServerSideProps : GetServerSideProps = async (context)=>
{
  var u2 = await redirectUrl(context.resolvedUrl);

    if (u2)
    {
      return {
        redirect: 
        {
          destination: "/" + u2,
          permanent: false
        }
      }
    }
    
    let user = await getUserInformation(context?.req?.headers.cookie);
    let dt = DateTime.now().toUTC().toISO();
    
    let token;

    if (user.subscribed)
    {
        token = await getJWTTokenFromUser(user);
    }

    const slug = context.params?.video as string;
    const password = context.query?.password as string;

    const q = encode(context.query);

    //need to pass on eg the campaign
    const params = (q.length==0?'':'?') + q;

    let vid = await getVideoFromSlug(slug, token, password);

    if (!vid)
    {

      return {
        notFound: true
      }
    }

    let live = vid.isLiveSession;
    
    if (vid.inTheFuture && live)
    {

      if (vid.accessible && user && user.subscribed)
      {
        return {
          redirect:
          {
            destination: `/live/${vid.privateRedirect}${params}`,
            permanent:false
          }

        }

      }
      else
      {
        if (vid.bookingUrl)
        {
          return {
            redirect:
            {
              destination: `/booking/${vid.slug}${params}`,
              permanent:false
            }
        }
      }
    }
  }

  if (vid.isSeries)
  {
    let options = { 
      method: 'GET',  cache: "no-store" as RequestCache
      };

      if (token)
      {
          (options as any).headers = {'auth-token': token}
      }

    const seriesVideos: VideoSearchResult = await (await fetch(process.env.ADMIN_ROUTE + "/videosinseries?id=" + vid.id, options)).json();
    let trailer : Video | null = null;

    let output : Video [] = [];

    seriesVideos.videos.forEach(x=>
      {
        if (x.isTrailer)
        {
          trailer=x;

        }
        else
        {
          output.push(x);
        }
      });
    return {
      props: {
        data: 
        { 
          video:vid,
          videosInSeries: output,
          trailer:trailer,
          dateProcessed: dt
        }
      }
  }
  }
  else
  {
    let options = { 
      method: 'GET',  cache: "no-store" as RequestCache
      };

    const relatedVideos: VideoSearchResult = await (await fetch(process.env.ADMIN_ROUTE + "/relatedvideos?id=" + vid.id, options)).json();
    const ancillaryLinks: VideoAncillaryLink[] = await getVideoAncillaryLinks(vid.id);
  
    return {
      props: {
        data: 
        { 
          video:vid,
          relatedVideos: relatedVideos.videos,
          ancillaryLinks: ancillaryLinks,
          dateProcessed: dt
        }
      }
    }
  }

  



}